import FeatureLayer from '@arcgis/core/layers/FeatureLayer.js';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer.js';
import Graphic from '@arcgis/core/Graphic.js';
import type MapView from '@arcgis/core/views/MapView.js';
import type FeatureLayerView from '@arcgis/core/views/layers/FeatureLayerView.js';
import type Point from '@arcgis/core/geometry/Point.js';
import type Extent from '@arcgis/core/geometry/Extent.js';
import { useSelectionStore, type SelectionDetail } from '@/state/selectionStore';
import { findLayer } from '@/map/layers/registry';
import { symbolForGeometry } from '@/map/symbols';

const RESERVED_LAYER_IDS: ReadonlySet<string> = new Set([
  '__rcmc_highlights',
  '__rcmc_user_selection',
  '__rcmc_buffers',
  '__rcmc_markers',
]);

function isFeatureHit(layer: unknown): layer is FeatureLayer {
  if (!(layer instanceof FeatureLayer)) return false;
  return !RESERVED_LAYER_IDS.has(layer.id);
}

function centroidOf(geom: __esri.Geometry | null | undefined): Point | null {
  if (!geom) return null;
  if (geom.type === 'point') return geom as Point;
  const asPolygon = geom as unknown as { centroid?: Point };
  if (asPolygon.centroid && asPolygon.centroid.x != null) return asPolygon.centroid;
  const ext = (geom as { extent?: Extent }).extent;
  if (ext?.center) return ext.center;
  return null;
}

function drawSelectionGraphic(layer: GraphicsLayer, geometry: __esri.Geometry): void {
  layer.removeAll();
  layer.add(
    new Graphic({
      geometry,
      symbol: symbolForGeometry(geometry.type, 'selection'),
    }),
  );
}

export function clearUserSelection(selectionLayer: GraphicsLayer | null): void {
  if (selectionLayer) selectionLayer.removeAll();
  useSelectionStore.getState().clear();
}

export function installHoverHighlight(view: MapView): () => void {
  let currentHandle: { remove: () => void } | null = null;
  let currentKey: string | null = null;
  let currentCursor: string | null = null;
  let hoverSeq = 0;

  const container = view.container as HTMLElement | undefined;
  const setCursor = (value: string) => {
    if (currentCursor === value) return;
    currentCursor = value;
    if (container) container.style.cursor = value;
  };

  const clear = () => {
    if (currentHandle) {
      currentHandle.remove();
      currentHandle = null;
      currentKey = null;
    }
  };

  const layerViewCache = new WeakMap<FeatureLayer, FeatureLayerView>();
  let pendingPoint: { x: number; y: number } | null = null;
  let rafId: number | null = null;

  const handleMove = async (point: { x: number; y: number }) => {
    const seq = ++hoverSeq;
    let hit;
    try {
      hit = await view.hitTest(point);
    } catch {
      return;
    }
    if (seq !== hoverSeq) return;

    const result = hit.results.find((r) => {
      const layer = (r as { graphic?: Graphic }).graphic?.layer;
      return isFeatureHit(layer);
    }) as { graphic?: Graphic } | undefined;

    if (!result?.graphic) {
      clear();
      setCursor('default');
      return;
    }

    setCursor('pointer');
    const graphic = result.graphic;
    const layer = graphic.layer as FeatureLayer;
    const oid = graphic.getObjectId?.();
    const fallbackId = (graphic as unknown as { uid?: string | number }).uid;
    const key = `${layer.id}:${oid ?? fallbackId ?? ''}`;
    if (key === currentKey) return;

    try {
      let lv = layerViewCache.get(layer);
      if (!lv) {
        lv = (await view.whenLayerView(layer)) as FeatureLayerView;
        layerViewCache.set(layer, lv);
      }
      if (seq !== hoverSeq) return;
      clear();
      currentHandle = lv.highlight(graphic);
      currentKey = key;
    } catch {
      // layer view not ready
    }
  };

  const moveHandle = view.on('pointer-move', (evt) => {
    pendingPoint = { x: evt.x, y: evt.y };
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      const p = pendingPoint;
      pendingPoint = null;
      if (p) void handleMove(p);
    });
  });

  const leaveHandle = view.on('pointer-leave', () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    pendingPoint = null;
    hoverSeq++;
    clear();
    setCursor('default');
  });

  return () => {
    moveHandle.remove();
    leaveHandle.remove();
    if (rafId !== null) cancelAnimationFrame(rafId);
    clear();
  };
}

export function installClickSelect(view: MapView, selectionLayer: GraphicsLayer): () => void {
  let clickSeq = 0;
  const handle = view.on('click', async (evt) => {
    const seq = ++clickSeq;
    let hit;
    try {
      hit = await view.hitTest(evt);
    } catch {
      return;
    }
    if (seq !== clickSeq) return;

    const featureHit = hit.results.find((r) => {
      const layer = (r as { graphic?: Graphic }).graphic?.layer;
      return isFeatureHit(layer);
    }) as { graphic?: Graphic } | undefined;

    if (!featureHit?.graphic) {
      clearUserSelection(selectionLayer);
      return;
    }

    await recordSelection(featureHit.graphic, selectionLayer);
  });

  return () => {
    handle.remove();
  };
}

async function recordSelection(graphic: Graphic, selectionLayer: GraphicsLayer): Promise<void> {
  const layer = graphic.layer;
  if (!isFeatureHit(layer)) return;

  const record = findLayer(layer.id);
  const oidField = record?.probe?.service.object_id_field ?? layer.objectIdField ?? 'objectid';
  const displayField = record?.probe?.service.display_field;

  const oid = graphic.getObjectId?.() ?? graphic.attributes?.[oidField];
  if (oid == null) return;

  // hitTest returns geometry; only re-query when we lack the full attribute set.
  let attributes: Record<string, unknown> = { ...(graphic.attributes ?? {}) };
  let geometry: __esri.Geometry | null = graphic.geometry ?? null;

  const knownFieldCount = Object.keys(attributes).length;
  const probedFieldCount = record?.probe?.fields.length ?? 0;
  const needsFullAttributes = probedFieldCount > 0 && knownFieldCount < probedFieldCount * 0.6;

  if (!geometry || needsFullAttributes) {
    try {
      const where = `${oidField} = ${typeof oid === 'string' ? `'${oid}'` : oid}`;
      const rs = await layer.queryFeatures({
        where,
        outFields: ['*'],
        returnGeometry: !geometry,
        num: 1,
      });
      const feature = rs.features[0];
      if (feature) {
        attributes = feature.attributes ?? attributes;
        geometry = feature.geometry ?? geometry;
      }
    } catch {
      // fall back to whatever hit-test gave us
    }
  }

  if (!geometry) return;

  const display = displayField
    ? (attributes[displayField] as string | number | undefined)
    : undefined;
  const centroid = centroidOf(geometry);
  const detail: SelectionDetail = {
    layer_id: layer.id,
    objectid: oid,
    display:
      display != null && String(display).trim() !== '' ? String(display) : undefined,
    attributes,
    centroid: centroid
      ? {
          x: centroid.x,
          y: centroid.y,
          wkid: centroid.spatialReference?.wkid ?? undefined,
        }
      : undefined,
  };

  drawSelectionGraphic(selectionLayer, geometry);
  useSelectionStore.getState().set(detail, geometry);
}
